import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  FormControlLabel,
  Radio,
  TextField,
  InputAdornment,
  Button,
  Paper,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  SET_LOADING_VIEWER,
  SET_VIEWER_FILTER,
} from "../../store/actionTypes/viewerActionTypes";
import dayjs from "dayjs";
import {
  getViewerStatusMinuteDiff,
  getViewerUpdatedAtMinuteDiff,
} from "../../helpers/viewerHelper";
import { colorStyling } from "../../helpers/color";
import SearchSelectDropdown from "../searchSelectDropdown/SearchSelectDropdown";
import { contentCMSAPILimit, groupAPILimit, scheduleAPILimit } from "../../api";
import { getScheduleQuery } from "../../helpers/scheduleHelper";

const circleItem = (color, key, size) => {
  return (
    <Box
      key={key}
      sx={{
        width: size || 30,
        height: size || 30,
        borderRadius: "50%",
        m: 0.5,
        backgroundColor: color,
      }}
    />
  );
};

const trafficLightView = (type) => {
  let colorState = {};
  switch (type) {
    case "OFFLINE":
      colorState = {
        left: colorStyling.traffic.gray,
        middle: colorStyling.traffic.gray,
        right: colorStyling.traffic.gray,
      };
      break;

    case "INACTIVE":
      colorState = {
        left: colorStyling.traffic.red,
        middle: colorStyling.traffic.gray,
        right: colorStyling.traffic.gray,
      };
      break;

    case "NODISPLAY":
      colorState = {
        left: colorStyling.traffic.gray,
        middle: colorStyling.traffic.yellow,
        right: colorStyling.traffic.gray,
      };
      break;

    case "ACTIVE":
      colorState = {
        left: colorStyling.traffic.gray,
        middle: colorStyling.traffic.gray,
        right: colorStyling.traffic.green,
      };
      break;

    default:
      break;
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      {circleItem(colorState.left, "tl", 15)}
      {circleItem(colorState.middle, "tm", 15)}
      {circleItem(colorState.right, "tr", 15)}
    </Box>
  );
};

const FilterViewer = () => {
  const dispatch = useDispatch();
  const { viewerFilters } = useSelector((state) => state.viewer);
  const { features } = useSelector((state) => state.auth);

  const { t } = useTranslation("viewer");
  const { t: tCommon } = useTranslation();

  const [viewerName, setViewerName] = useState(viewerFilters.nameLike);
  const [viewerLocation, setViewerLocation] = useState(
    viewerFilters.addressLike
  );
  const [status, setStatus] = useState("ANY");
  const [selectGroup, setSelectGroup] = useState("");
  const [selectSchedule, setSelectSchedule] = useState("");
  const [selectContent, setSelectContent] = useState("");

  const [viewerVersion, setViewerVersion] = useState(viewerFilters.version);

  const [anchorEl, setAnchorEl] = useState(null);
  const [openAdvancedFilter, setOpenAdvancedFilter] = useState(false);

  const openSortButton = Boolean(anchorEl);

  const handleToggleAdvancedFilter = () => {
    setOpenAdvancedFilter(!openAdvancedFilter);
  };

  const handleOpenSort = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSort = () => {
    setAnchorEl(null);
  };

  const handleDispatchFilter = (payload) => {
    dispatch({
      type: SET_VIEWER_FILTER,
      payload,
    });
  };

  const handleLoading = (value) => {
    dispatch({
      type: SET_LOADING_VIEWER,
      payload: value,
    });
  };

  const handleChangeSortBy = (value) => {
    handleLoading(true);
    handleDispatchFilter({ ...viewerFilters, sortBy: value });
  };

  const handleChangeStatus = (event) => {
    const value = event.target.value;
    setStatus(value);
  };

  const handleRefresh = () => {
    handleDispatchFilter({ sortBy: "idDesc" });

    setViewerName("");
    setViewerLocation("");
    setViewerVersion("");
    setStatus("ANY");
    setSelectGroup("");
    setSelectSchedule("");
    setSelectContent("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    let query = { ...viewerFilters };

    if (viewerName) query["nameLike"] = viewerName;
    else delete query["nameLike"];

    if (viewerLocation) query["addressLike"] = viewerLocation;
    else delete query["addressLike"];

    if (viewerVersion) query["version"] = viewerVersion;
    else delete query["version"];

    if (selectGroup) query["screenGroupId"] = selectGroup?.id;
    else delete query["screenGroupId"];

    if (selectSchedule) query["scheduleId"] = selectSchedule?.id;
    else delete query["scheduleId"];

    if (selectContent) query["contentId"] = selectContent?.id;
    else delete query["contentId"];

    switch (status) {
      case "OFFLINE":
        query["activeAtLte"] = dayjs()
          .subtract(getViewerStatusMinuteDiff(), "m")
          .toISOString();
        delete query["statusUpdatedAtLte"];
        delete query["statusUpdatedAtGte"];
        delete query["hasNoCurrentSchedule"];
        delete query["activeAtGte"];
        delete query["displayStatus"];
        break;

      case "INACTIVE":
        query["statusUpdatedAtLte"] = dayjs()
          .subtract(getViewerUpdatedAtMinuteDiff(), "m")
          .toISOString();
        query["activeAtGte"] = dayjs()
          .subtract(getViewerStatusMinuteDiff(), "m")
          .toISOString();
        query["activeAtLte"] = dayjs()
          .add(getViewerStatusMinuteDiff(), "m")
          .toISOString();
        delete query["statusUpdatedAtGte"];
        delete query["hasNoCurrentSchedule"];
        delete query["displayStatus"];
        break;

      case "NODISPLAY":
        query["statusUpdatedAtGte"] = dayjs()
          .subtract(getViewerUpdatedAtMinuteDiff(), "m")
          .toISOString();
        query["activeAtGte"] = dayjs()
          .subtract(getViewerStatusMinuteDiff(), "m")
          .toISOString();
        query["activeAtLte"] = dayjs()
          .add(getViewerStatusMinuteDiff(), "m")
          .toISOString();
        query["displayStatus"] = "OFF";
        delete query["hasNoCurrentSchedule"];
        delete query["statusUpdatedAtLte"];
        break;

      case "ACTIVE":
        query["hasNoCurrentSchedule"] = false;
        query["statusUpdatedAtGte"] = dayjs()
          .subtract(getViewerStatusMinuteDiff(), "m")
          .toISOString();
        query["activeAtGte"] = dayjs()
          .subtract(getViewerStatusMinuteDiff(), "m")
          .toISOString();
        query["activeAtLte"] = dayjs()
          .add(getViewerStatusMinuteDiff(), "m")
          .toISOString();
        delete query["statusUpdatedAtLte"];
        delete query["displayStatus"];
        break;

      default:
        delete query["statusUpdatedAtGte"];
        delete query["statusUpdatedAtLte"];
        delete query["hasNoCurrentSchedule"];
        delete query["connectionStatus"];
        delete query["activeAtGte"];
        delete query["activeAtLte"];
        delete query["displayStatus"];
        break;
    }

    handleLoading(true);
    handleDispatchFilter(query);
  };

  return (
    <Box>
      <Paper
        elevation={2}
        sx={{ p: 2, mt: 2, borderRadius: "6px" }}
        component="form"
        onSubmit={handleSubmit}
      >
        <Box sx={{ display: "flex" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <TextField
              label={t("filter.searchNameLabel")}
              fullWidth
              type="search"
              size="small"
              sx={{ mr: 2 }}
              value={viewerName}
              placeholder={t("filter.searchPlaceholder")}
              onChange={(event) => setViewerName(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: "150px" }}>
              <InputLabel id="select-label">Status</InputLabel>
              <Select
                labelId="select-label"
                label="Status"
                select
                SelectProps={{
                  native: true,
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
                value={status}
                onChange={handleChangeStatus}
              >
                <MenuItem value="ANY">
                  <Box>{t("filter.statusAny")}</Box>
                </MenuItem>
                <MenuItem value="OFFLINE">
                  {trafficLightView("OFFLINE")}
                </MenuItem>
                <MenuItem value="INACTIVE">
                  {trafficLightView("INACTIVE")}
                </MenuItem>
                <MenuItem value="NODISPLAY">
                  {trafficLightView("NODISPLAY")}
                </MenuItem>
                <MenuItem value="ACTIVE">{trafficLightView("ACTIVE")}</MenuItem>
              </Select>
            </FormControl>

            <Button sx={{ ml: 2 }} variant="contained" type="submit">
              {tCommon("searchBtn")}
            </Button>
          </Box>

          <Box sx={{ display: "flex", ml: 2 }}>
            <IconButton
              sx={{ color: colorStyling.black }}
              onClick={handleOpenSort}
              aria-controls={openSortButton ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={openSortButton ? "true" : undefined}
            >
              <Tooltip title={tCommon("sortTip")}>
                <SortIcon fontSize="small" />
              </Tooltip>
            </IconButton>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={openSortButton}
              onClose={handleCloseSort}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem onClick={() => handleChangeSortBy("activeAtDesc")}>
                <FormControlLabel
                  disableTypography
                  value="activeAtDesc"
                  control={
                    <Radio
                      size="small"
                      checked={viewerFilters.sortBy === "activeAtDesc"}
                    />
                  }
                  label={t("filter.sortByMostActive")}
                  sx={{ fontSize: "14px", fontFamily: "Inter, sans-serif" }}
                />
              </MenuItem>
              <MenuItem onClick={() => handleChangeSortBy("activeAtAsc")}>
                <FormControlLabel
                  disableTypography
                  value="activeAtAsc"
                  control={
                    <Radio
                      size="small"
                      checked={viewerFilters.sortBy === "activeAtAsc"}
                    />
                  }
                  label={t("filter.sortByLeastActive")}
                  sx={{ fontSize: "14px", fontFamily: "Inter, sans-serif" }}
                />
              </MenuItem>
              <MenuItem onClick={() => handleChangeSortBy("idAsc")}>
                <FormControlLabel
                  disableTypography
                  value="idAsc"
                  control={
                    <Radio
                      size="small"
                      checked={viewerFilters.sortBy === "idAsc"}
                    />
                  }
                  label={t("filter.sortByOldest")}
                  sx={{ fontSize: "14px", fontFamily: "Inter, sans-serif" }}
                />
              </MenuItem>
              <MenuItem onClick={() => handleChangeSortBy("idDesc")}>
                <FormControlLabel
                  disableTypography
                  value="idDesc"
                  control={
                    <Radio
                      size="small"
                      checked={viewerFilters.sortBy === "idDesc"}
                    />
                  }
                  label={t("filter.sortByNewest")}
                  sx={{ fontSize: "14px", fontFamily: "Inter, sans-serif" }}
                />
              </MenuItem>
              <MenuItem onClick={() => handleChangeSortBy("nameAsc")}>
                <FormControlLabel
                  disableTypography
                  value="nameAsc"
                  control={
                    <Radio
                      size="small"
                      checked={viewerFilters.sortBy === "nameAsc"}
                    />
                  }
                  label={t("filter.sortByAtoZ")}
                  sx={{ fontSize: "14px", fontFamily: "Inter, sans-serif" }}
                />
              </MenuItem>
              <MenuItem onClick={() => handleChangeSortBy("nameDesc")}>
                <FormControlLabel
                  disableTypography
                  value="nameDesc"
                  control={
                    <Radio
                      size="small"
                      checked={viewerFilters.sortBy === "nameDesc"}
                    />
                  }
                  label={t("filter.sortByZtoA")}
                  sx={{ fontSize: "14px", fontFamily: "Inter, sans-serif" }}
                />
              </MenuItem>
            </Menu>

            <IconButton
              sx={{ ml: 2, color: colorStyling.black }}
              color="primary"
              onClick={handleRefresh}
            >
              <Tooltip title={tCommon("resetFilterTip")}>
                <RestartAltIcon fontSize="small" />
              </Tooltip>
            </IconButton>
          </Box>
        </Box>
        {openAdvancedFilter && (
          <Box sx={{ mt: 2 }}>
            <Box display={"flex"}>
              <TextField
                label={t("filter.searchLocationLabel")}
                fullWidth
                type="search"
                size="small"
                sx={{ mb: 2, mr: 2 }}
                value={viewerLocation}
                placeholder={t("filter.searchLocationPlaceholder")}
                onChange={(event) => setViewerLocation(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label={t("filter.searchVersionLabel")}
                fullWidth
                type="search"
                size="small"
                sx={{ mb: 2 }}
                value={viewerVersion}
                placeholder={t("filter.searchVersionPlaceholder")}
                onChange={(event) => setViewerVersion(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <SearchSelectDropdown
              sx={{ mb: 2 }}
              label={t("filter.searchGroupLabel")}
              placeholder={t("filter.searchGroupPlaceholder")}
              selectedItem={selectGroup}
              selectItem={setSelectGroup}
              urlLimit={groupAPILimit}
            />

            <SearchSelectDropdown
              sx={{ mb: 2 }}
              label={t("filter.searchScheduleLabel")}
              placeholder={t("filter.searchSchedulePlaceholder")}
              selectedItem={selectSchedule}
              selectItem={setSelectSchedule}
              urlLimit={scheduleAPILimit + getScheduleQuery(features, true)}
            />

            <SearchSelectDropdown
              label={t("filter.searchContentLabel")}
              placeholder={t("filter.searchContentPlaceholder")}
              selectedItem={selectContent}
              selectItem={setSelectContent}
              urlLimit={contentCMSAPILimit}
            />
          </Box>
        )}
      </Paper>

      <Box sx={{ mb: 2, ml: 2 }}>
        <Tooltip arrow disableInteractive title={t("filter.openAdvancedTip")}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleToggleAdvancedFilter}
            sx={{ p: 0, mt: "-4px", borderRadius: "0 0 10px 10px" }}
          >
            {openAdvancedFilter ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default FilterViewer;
